import Immutable from "immutable";
import classnames from "classnames";
import { Check, ChevronRight } from "@mui/icons-material";

import { useDialogState } from "../../../hooks/useDialogState";
import { EntryDialog as OldEntryDialog } from "../../eventDivisions/entryDialog";
import { Confirmed } from "./Confirmed";
import { ReinstateButton } from "./ReinstateButton";
import { RemoveButton } from "./RemoveButton";
import { DenyButton } from "./DenyButton";
import { useRowStyles } from "../commonStyles/tableStyles";
import { EditSeedForm } from "./EditSeedForm";
import { AthleteCell } from "../tables/AthleteCell";
import { makeStyles } from "../../providers/makeStyles";
import { useEvent } from "../../routes/events/id/EventProvider";
import { useConvertToImmutable } from "../../../hooks/useConvertToImmutable";
import { Waitlisted } from "./Waitlisted";
import { Checkbox } from "../forms/fields/Checkbox";
import { Box } from "@mui/material";
import { memo } from "react";
import { EntryDialog } from "components/routes/events/id/divisions/eventDivisionId/entries/EntryDialog";
import { recursiveSnakeToCamel } from "utils/utils";
import { useFeatureFlags } from "components/providers/featureFlags/useFeatureFlags";

const statusToComponent = {
    confirmed: Confirmed,
    waitlisted: Waitlisted,
    denied: ReinstateButton
};

const useStyles = makeStyles(theme => ({
    row: {
        cursor: "pointer",
        "&.highlight": {
            backgroundColor: theme.palette.success.lighter,
        },
        "& td:nth-of-type(1)": {
            padding: theme.spacing(2, 0),
        }
    },
    bib: {
        marginLeft: theme.spacing(2),
        [theme.breakpoints.down("md")]: {
            marginLeft: theme.spacing(1),
        }
    },
    seed: {
        "&&": {
            paddingRight: theme.spacing(2)
        },
        [theme.breakpoints.down("md")]: {
            textAlign: "center"
        }
    },
    chevron: {
        "&&": {
            padding: theme.spacing(0, 1)
        }
    }
}));

const ManageEntryRowComponent = ({ entry: jsEntry, highlight, showBib, isTeams, showSeed, seedIsPosition, selected, toggleEntries, bulkSelectEnabledForMobile, hasUnconfirmedEntries, index, isMobile, teamRoles }) => {
    const entry = useConvertToImmutable(jsEntry, ["properties"]);
    const captureProperties = (useEvent().get("capture_fields") || Immutable.List()).toJS();
    const eventDivisionId = entry.get("event_division_id");
    const [dialogOpen, openDialog, closeDialog] = useDialogState();
    const StatusComponent = statusToComponent[entry.get("status")];
    const entryId = entry.get("id");
    const athlete = entry.get("athlete");
    const classes = { ...useStyles(), ...useRowStyles() };
    const newEntryForm = useFeatureFlags();

    const onClick = e => {
        e.stopPropagation();
    };

    return (
        <>
            <tr className={classnames(classes.row, { highlight: highlight && entry.get("status") === "confirmed" })} onClick={openDialog}>
                {hasUnconfirmedEntries && (!isMobile || bulkSelectEnabledForMobile) && <td><Box component="span" pl={{ lg: 1, md: 0 }}>
                    <Checkbox
                        input={{
                            onClick: onClick,
                            onChange: e => toggleEntries(e, index, e.target.checked),
                            value: selected
                        }}
                        Icon={Check} />
                </Box></td>}
                {showBib && <td>{entry.get("bib") && <div className={classnames(classes.bib, "bib")}>{entry.get("bib")}</div>}</td>}

                <AthleteCell name={athlete.get("name")} image={athlete.get("image")} team={entry.get("team_name")} hideAvatar={isMobile}/>

                {showSeed &&
                <td className={classes.seed}>
                    {isMobile ? (entry.get("seed") || entry.get("rank")) : <EditSeedForm entry={entry}/>}
                </td>}
                <td>
                    <StatusComponent entry={jsEntry} isTeams={isTeams}/>
                </td>

                {!isMobile &&
                <td className={classes.chevron}>
                    <ChevronRight/>
                </td>}
            </tr>

            {newEntryForm
                ? <EntryDialog
                    open={dialogOpen}
                    onClose={closeDialog}
                    entry={recursiveSnakeToCamel(entry.toJS())}
                    seedIsPosition={seedIsPosition}
                    showSeed={showSeed}
                    showBib={showBib}
                />
                : <OldEntryDialog
                    open={dialogOpen}
                    eventDivisionId={eventDivisionId}
                    isTeams={isTeams}
                    captureProperties={captureProperties}
                    entry={entry}
                    onClose={closeDialog}
                    showBib={showBib}
                    showSeed={showSeed}
                    seedIsPosition={seedIsPosition}
                    teamRoles={teamRoles}
                    removeButton={entry.get("status") === "denied"
                        ? <RemoveButton eventDivisionId={eventDivisionId} entryId={entryId} closeModal={closeDialog}/>
                        : <DenyButton eventDivisionId={eventDivisionId} entryId={entryId} isTeams={isTeams} closeModal={closeDialog}/>
                    }
                />}
        </>
    );
};

export const ManageEntryRow = memo(ManageEntryRowComponent, (prevProps, nextProps) => {
    const primitives = [
        "highlight",
        "showBib",
        "isTeams",
        "showSeed",
        "seedIsPosition",
        "selected",
        "bulkSelectEnabledForMobile",
        "hasUnconfirmedEntries",
        "index",
        "isMobile",
        "toggleEntries" // function wrapped in useCallback
    ];
    if (primitives.some(prop => prevProps[prop] !== nextProps[prop])) return false;

    if (JSON.stringify(prevProps.entry) !== JSON.stringify(nextProps.entry)) return false;
    if (JSON.stringify(prevProps.teamRoles) !== JSON.stringify(nextProps.teamRoles)) return false;

    return true;
});
